import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { Box, Column, Typography } from 'seagull';
import { AnchorLogo } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { redirect, routes } from '~/constants';
import { useEventReporter, useIoContext, useOverlayContext, useStoreActions, useStoreStatic } from '~/contexts';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useFrontendLogging } from '~/hooks/useFrontendLogging';
import { useOnMount } from '~/hooks/useOnMount';
import { useLocaleKeys } from '~/i18n';
import { ErrorResponse, ServerError } from '~/infra/ErrorResponse';
import styles from './Notification.module.scss';
import { buildNotificationFailureFingerprint, readNotificationLoadFailureLog } from './notificationLoadDiagnostics';
import { notificationRouter } from './notificationRouter';
const acceptNotificationLoader = 'acceptNotification';
const invalidInvitationError = 'invalidInvitation';
export const GetNotificationTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            NotificationTID: {
                InvalidInvitation: 'invalid-invitation'
            }
        }
    });
function InvalidInvitationIndicator() {
    const { t: notificationTexts } = useLocaleKeys('pages.notification');
    const { NotificationTID } = useMemo(()=>GetNotificationTID(), []);
    return /*#__PURE__*/ _jsx(Box, {
        className: styles.invalidWrapper,
        testId: NotificationTID.InvalidInvitation(),
        children: /*#__PURE__*/ _jsxs(Column, {
            className: styles.content,
            alignItems: "center",
            children: [
                /*#__PURE__*/ _jsx(AnchorLogo, {
                    className: styles.logo
                }),
                /*#__PURE__*/ _jsx(Typography, {
                    variant: "h3",
                    weight: "normal",
                    children: notificationTexts.invalidInvitationTitle()
                }),
                /*#__PURE__*/ _jsx(Typography, {
                    variant: "h5",
                    children: notificationTexts.invalidInvitationContact()
                }),
                /*#__PURE__*/ _jsx(Typography, {
                    variant: "h5",
                    children: notificationTexts.supportEmailContact()
                })
            ]
        })
    });
}
export function Notification() {
    const { notificationId = '' } = useParams();
    const [error, setError] = useState();
    const featureFlags = useFeatureFlags();
    const { reportToHeap, reportToGtm, reportToAppcues, reportToHubspot } = useEventReporter();
    const { getState } = useStoreStatic();
    const { api, setAuthToken } = useStoreActions();
    const overlayContext = useOverlayContext();
    const { t: notificationTexts } = useLocaleKeys('pages.notification');
    const location = useLocation();
    const navigate = useNavigate();
    const { browser } = useIoContext();
    const reportFrontendLog = useFrontendLogging();
    useOnMount(()=>{
        async function handleAuthenticationAndRedirect() {
            const isLoggedIn = !!getState().authToken;
            overlayContext.startLoader(acceptNotificationLoader);
            let redirectToUrl, stateChanges;
            try {
                const searchParams = new URLSearchParams(location.search);
                const intent = searchParams.get('intent') || undefined;
                const notification = await (isLoggedIn ? api.notification.getWhileLoggedIn(notificationId, intent) : api.notification.getWhileLoggedOut(notificationId, intent));
                if (notification.authToken != null) await setAuthToken(notification.authToken);
                if (notification && notification.notificationType) {
                    reportToGtm('notification-open', notification.notificationType);
                    reportToAppcues('notification-open', notification.notificationType);
                    reportToHeap('notification', 'open', notification.notificationType);
                }
                const { redirect, error, hubspotEventName } = notificationRouter({
                    notification,
                    claims: getState().claims,
                    userInfo: getState().userInfo,
                    notificationId
                });
                hubspotEventName && reportToHubspot(hubspotEventName);
                redirectToUrl = redirect.url;
                stateChanges = redirect.state;
                if (error) {
                    const loadFailure = readNotificationLoadFailureLog({
                        browser,
                        notificationId,
                        routerError: error,
                        payload: notification
                    });
                    if (loadFailure != null && getState().authToken != null) reportFrontendLog(loadFailure);
                    const err = new Error(error + " - type:".concat(notification.type || '(empty)', ";notificationType:").concat(notification.notificationType || '(empty)'));
                    Sentry.withScope((scope)=>{
                        scope.setFingerprint(buildNotificationFailureFingerprint(loadFailure));
                        scope.setContext('Notification data', {
                            notificationPayload: JSON.stringify(notification)
                        });
                        if (loadFailure != null) scope.setContext('Notification load', _object_spread({}, loadFailure));
                        Sentry.captureException(err);
                    });
                    overlayContext.showErrorToast(notificationTexts.errorRedirectToast(), 'notification-redirect-toast');
                }
            } catch (err) {
                const e = new ErrorResponse(err);
                if (e.status === 401) {
                    if (isLoggedIn) {
                        setAuthToken(null);
                    }
                    const loginFlow = e.getLoginFlowData();
                    const recipientEmail = e.getRecipientEmail();
                    const accessProposal = featureFlags.SHARED_PROPOSAL_LINK && e.isSharedProposalLinkFlow();
                    if (loginFlow || recipientEmail || accessProposal) {
                        stateChanges = _object_spread({}, loginFlow && {
                            setLoginFlow: loginFlow
                        }, recipientEmail && {
                            recipientEmail
                        }, accessProposal && {
                            accessProposal: true
                        });
                    }
                    redirectToUrl = redirect.getPathAndSearch({
                        returnTo: location.pathname
                    });
                } else if (e.status === 403) {
                    const recipientEmail = e.getRecipientEmail();
                    const sourceUrl = location.pathname;
                    const notificationNoAccessState = {
                        recipientEmail,
                        returnTo: sourceUrl
                    };
                    stateChanges = notificationNoAccessState;
                    redirectToUrl = routes.noAccessPage;
                } else if (e.hasServerError(ServerError.RecipientNotFound) && isLoggedIn) {
                    // logout flow will trigger an unmount/mount cycle because this component renders only when authFlowDone is true.
                    await setAuthToken(null);
                } else if (e.hasServerError(ServerError.InvalidTeamInvitation)) {
                    setError(invalidInvitationError);
                } else {
                    redirectToUrl = routes.home;
                }
            }
            if (redirectToUrl) {
                navigate(redirectToUrl, {
                    replace: true,
                    state: stateChanges
                });
            }
            overlayContext.stopLoader(acceptNotificationLoader);
        }
        handleAuthenticationAndRedirect();
    });
    return error === invalidInvitationError ? /*#__PURE__*/ _jsx(InvalidInvitationIndicator, {}) : null;
}
